import { queryClient } from "@/main"
import { useEffect, useMemo, useState } from "react"
import { AttendanceContextDrawer } from "../../components/attendance/AttendanceContextDrawer"
import { YoungsFilterSearchBar } from "../../components/filters/YoungsFilterSearchBar"
import { EditedAttendanceStatusFilterBar, EditedAttendanceStatusFilterBarProps } from "@/components/filters/EditedAttendanceStatusFilterBar"
import { OnChangeFnParamsType, OneYoungForRowCall } from "../../components/attendance/OneYoungForRowCall"
import { QueryRender } from "../../components/helpers/QueryRender"
import { useProcess } from "../../contexts/ProcessContext"
import { useTimeMachine } from "../../contexts/TimeMachineContext"
import { handleFaireLAppelSubmit } from "../../controller/handleFaireLAppelSubmit"
import { useCreateAttendanceEvent, useUpdateAttendanceEvent } from "../../entities/attendanceEvent/useAttendanceEvent"
import { invalidateQueriesForStatisticsAboutYoungsWithAttendance, useYoungsWithAttendanceForRowCall, YoungWithAttendanceForRowCall } from "../../entities/user/useYoung"
import { useCurrentSchoolYear } from "../../entities/schoolYear/useSchoolYear"
import { useAuth } from "../../hooks/useAuth"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { AttendanceStatus } from "../../types/attendanceStatus"
import { AttendanceTime } from "../../types/AttendanceTime"

export type YoungWithAttendanceAndMetadata = YoungWithAttendanceForRowCall & {
   newStatus: AttendanceStatus
   newExpected: boolean
   presentFromType: AttendanceTime
   presentFromTime: string
   lateFromType: AttendanceTime
   lateFromTime: string
   absenceFromType: AttendanceTime
   absenceFromTime: string
   absenceToType: AttendanceTime
   absenceToTime: string
}

export default function FaireLAppel() {
   const { now } = useTimeMachine()
   const { user, isLoading: isLoadingUser } = useAuth()
   const { startProcess } = useProcess()
   const createAttendanceEvent = useCreateAttendanceEvent()
   const updateAttendanceEvent = useUpdateAttendanceEvent()
   const { data: currentSchoolYear, isLoading: isLoadingCurrentSchoolYear } = useCurrentSchoolYear()
   const globalSettingsQuery = useGlobalSettings()
   const { data: originalYoungs = [], ...query } = useYoungsWithAttendanceForRowCall({ now })
   const [selectedGroups, setSelectedGroups] = useState<string[]>([])
   const [searchText, setSearchText] = useState("")
   const [selectedYoung, setSelectedYoung] = useState<YoungWithAttendanceAndMetadata | null>(null)
   const [shouldBounce, setShouldBounce] = useState(false)
   const [overrides, setOverrides] = useState<Record<string, YoungWithAttendanceAndMetadata>>({})

   // Nouveau filtre statut temporaire
   const [selectedEditedStatus, setSelectedEditedStatus] = useState<EditedAttendanceStatusFilterBarProps["selectedStatus"]>("all")

   const getYoungWithOverride = (youngId: string): YoungWithAttendanceAndMetadata => {
      const override = overrides[youngId]
      if (!override) {
         const originalYoung = originalYoungs.find((y) => y.id === youngId)!
         return {
            ...originalYoung,
            newStatus: originalYoung.current_attendance_status,
            newExpected: originalYoung.is_current_attendance_expected,
            presentFromType: "now",
            presentFromTime: "09:00",
            lateFromType: "start_of_day",
            lateFromTime: "09:00",
            absenceFromType: "start_of_day",
            absenceFromTime: "09:00",
            absenceToType: "end_of_day",
            absenceToTime: "17:00",
         } as YoungWithAttendanceAndMetadata
      }
      return override
   }

   const filteredYoungs = useMemo(
      () =>
         (originalYoungs || [])
            .filter(
               (young) =>
                  young.current_attendance_status !== "external" &&
                  (
                     selectedEditedStatus === "all" ||
                     getYoungWithOverride(young.id).newStatus === selectedEditedStatus
                  ) &&
                  selectedGroups.includes(young.group) &&
                  (!searchText ||
                     young.firstName.toLowerCase().startsWith(searchText.toLowerCase()) ||
                     young.lastName.toLowerCase().startsWith(searchText.toLowerCase())),
            )
            .sort((a, b) => a.firstName.localeCompare(b.firstName)),
      [originalYoungs, overrides, selectedGroups, searchText, selectedEditedStatus],
   )

 

   const updateYoungOverride = (
      youngId: string,
      updates: Partial<YoungWithAttendanceAndMetadata> | OnChangeFnParamsType,
   ) => {
      // Theses props are read-only
      if ("current_attendance_status" in updates && "is_current_attendance_expected" in updates) {
         delete updates.current_attendance_status
         delete updates.is_current_attendance_expected
      }
      setOverrides((prev) => ({
         ...prev,
         [youngId]: {
            ...getYoungWithOverride(youngId),
            ...updates,
         },
      }))
   }

   const handleYoungSelect = (young: YoungWithAttendanceForRowCall) => {
      setSelectedYoung(getYoungWithOverride(young.id))
      setShouldBounce(true)
      setTimeout(() => setShouldBounce(false), 300)
   }

   const handleSubmit = async () => {
      startProcess(async () => {
         if (!currentSchoolYear?.id) {
            throw new Error("No current school year set")
         }

         if (!globalSettingsQuery.data) {
            throw new Error("No global settings found")
         }

         setSelectedYoung(null)

         await handleFaireLAppelSubmit({
            youngsWithOverrides: originalYoungs.map((young) => getYoungWithOverride(young.id)),
            adminIdReporting: user!.id,
            now,
            currentSchoolYearId: currentSchoolYear.id,
            globalSettings: globalSettingsQuery.data,
            createAttendanceEvent: (data) => createAttendanceEvent(data, { quickMode: true }),
            updateAttendanceEvent: (id, data) => updateAttendanceEvent(id, data, { quickMode: true }),
         })

         // Reset form
         setOverrides({})

         queryClient.invalidateQueries({
            queryKey: ["collection", "users", "youngs", "with-attendance"],
         });
         
         invalidateQueriesForStatisticsAboutYoungsWithAttendance(true);
      })
   }

   useEffect(() => {
      if (selectedYoung) {
         // Force update the UI
         // TODO still useful?
         setSelectedYoung(getYoungWithOverride(selectedYoung.id))
      }
   }, [overrides])

   useEffect(() => {
      // Component is mounted/remounted

      invalidateQueriesForStatisticsAboutYoungsWithAttendance(true)

      return () => {
         // This cleanup function runs when the component unmounts
      }
   }, []) // Empty dependency array ensures this runs only on mount/unmount

   return (
      <div className="page-section">
         <h1 className="title">Faire l'appel</h1>

         <YoungsFilterSearchBar
            searchText={searchText}
            setSearchText={setSearchText}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
         />

         <EditedAttendanceStatusFilterBar
            selectedStatus={selectedEditedStatus}
            setSelectedStatus={setSelectedEditedStatus}
         />

         <QueryRender
            query={query}
            data={filteredYoungs}
            emptyMessage="Aucun jeune trouvé"
            showEmptyMessage={filteredYoungs.length === 0}
            treatFetchingAsLoading={true}
            isLoading={isLoadingCurrentSchoolYear || isLoadingUser}
         >
            {(filteredYoungs) => (
               <>
                  <div className="mt-8 mb-16 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-2 md:gap-4">
                     {filteredYoungs.map((young: YoungWithAttendanceForRowCall) => (
                        <OneYoungForRowCall
                           key={young.id}
                           status={getYoungWithOverride(young.id).newStatus}
                           statusExpected={getYoungWithOverride(young.id).newExpected}
                           originalStatus={young.current_attendance_status}
                           originalExpected={young.is_current_attendance_expected}
                           fullname={`${young.firstName} ${young.lastName}`}
                           avatar={young.photo}
                           onSelect={() => handleYoungSelect(young)}
                           onChange={(updates) => updateYoungOverride(young.id, updates)}
                        />
                     ))}
                  </div>

                  {/* Navbar with validation button */}
                  <div className="btm-nav p-10">
                     <div>
                        <button onClick={handleSubmit} className="btn btn-primary">
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6"
                           >
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 d="M4.5 12.75l6 6 9-13.5"
                              />
                           </svg>
                           Valider
                        </button>
                     </div>
                  </div>

                  <AttendanceContextDrawer
                     selectedYoung={selectedYoung}
                     onClose={() => setSelectedYoung(null)}
                     bounce={shouldBounce}
                     onChange={({
                        youngId,
                        data,
                     }: {
                        youngId: string
                        data: Partial<YoungWithAttendanceAndMetadata>
                     }) => {
                        updateYoungOverride(youngId, data)
                     }}
                  ></AttendanceContextDrawer>
               </>
            )}
         </QueryRender>
      </div>
   )
}
